import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  DeskFilters,
  NewTicket,
  SupportPhoto,
  SupportTicket,
  SupportTicketDetail,
  TicketCounts,
  TicketPriority,
  TicketStatus,
} from './types'

import { api } from '@/lib/api/client'
import { keys } from '@/lib/api/keys'

const base = '/support/v1/tickets'
const desk = '/admin/v1/tickets'

/** What the person raising tickets can do. Only ever their own. */
export const supportApi = {
  list: () => api.get<SupportTicket[]>(base),
  get: (id: string) => api.get<SupportTicketDetail>(`${base}/${id}`),
  unread: () => api.get<{ count: number }>(`${base}/unread-count`),
  create: (body: NewTicket) => api.post<SupportTicketDetail>(base, body),
  reply: (id: string, text: string, attachmentIds: string[] = []) =>
    api.post<SupportTicketDetail>(`${base}/${id}/messages`, {
      body: text,
      attachment_ids: attachmentIds,
    }),
  uploadPhoto: (file: File) => api.upload<SupportPhoto>(`${base}/attachments`, file, file.name),
  photo: (id: string) => api.download(`${base}/attachments/${id}`),
  close: (id: string) => api.post<SupportTicketDetail>(`${base}/${id}/close`),
}

/** What Convo staff can do with any ticket. */
export const deskApi = {
  list: (filters: DeskFilters) =>
    api.get<SupportTicket[]>(desk, {
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.unverified ? { unverified: true } : {}),
      ...(filters.needs_reply ? { needs_reply: true } : {}),
      ...(filters.kind ? { kind: filters.kind } : {}),
      ...(filters.priority ? { priority: filters.priority } : {}),
      ...(filters.search ? { search: filters.search } : {}),
    }),
  counts: () => api.get<TicketCounts>(`${desk}/counts`),
  get: (id: string) => api.get<SupportTicketDetail>(`${desk}/${id}`),
  verify: (id: string, note?: string) =>
    api.post<SupportTicketDetail>(`${desk}/${id}/verify`, note ? { note } : {}),
  reply: (id: string, text: string, internal: boolean, attachmentIds: string[] = []) =>
    api.post<SupportTicketDetail>(`${desk}/${id}/messages`, {
      body: text,
      internal,
      attachment_ids: attachmentIds,
    }),
  uploadPhoto: (file: File) => api.upload<SupportPhoto>(`${desk}/attachments`, file, file.name),
  photo: (id: string) => api.download(`${desk}/attachments/${id}`),
  setStatus: (id: string, status: TicketStatus, reason?: string) =>
    api.post<SupportTicketDetail>(`${desk}/${id}/status`, { status, ...(reason ? { reason } : {}) }),
  setPriority: (id: string, priority: TicketPriority) =>
    api.post<SupportTicketDetail>(`${desk}/${id}/priority`, { priority }),
  assign: (id: string, assigneeMe: boolean) =>
    api.post<SupportTicketDetail>(`${desk}/${id}/assign`, { assignee_me: assigneeMe }),
}

// ── Requester hooks ─────────────────────────────────────────────────────────

export function useMyTickets() {
  return useQuery({
    queryKey: keys.support.mine(),
    queryFn: supportApi.list,
    refetchInterval: 30_000,
  })
}

/** An open thread refreshes on its own, so a reply from Convo shows up without a reload. */
export function useMyTicket(id: string | undefined) {
  return useQuery({
    queryKey: keys.support.ticket(id ?? ''),
    queryFn: () => supportApi.get(id!),
    enabled: Boolean(id),
    refetchInterval: 8_000,
  })
}

export function useSupportUnread() {
  return useQuery({
    queryKey: keys.support.unread(),
    queryFn: supportApi.unread,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  })
}

function useRefreshSupport() {
  const qc = useQueryClient()
  return (ticket?: SupportTicketDetail) => {
    if (ticket) {
      qc.setQueryData(keys.support.ticket(ticket.id), ticket)
      qc.setQueryData(keys.admin.supportTicket(ticket.id), ticket)
    }
    void qc.invalidateQueries({ queryKey: keys.support.all })
    void qc.invalidateQueries({ queryKey: keys.admin.supportAll() })
  }
}

export function useCreateTicket() {
  const refresh = useRefreshSupport()
  return useMutation({ mutationFn: supportApi.create, onSuccess: (t) => refresh(t) })
}

export function useReplyToTicket(id: string) {
  const refresh = useRefreshSupport()
  return useMutation({
    mutationFn: ({ text, photoIds }: { text: string; photoIds: string[] }) =>
      supportApi.reply(id, text, photoIds),
    onSuccess: refresh,
  })
}

export function useCloseMyTicket(id: string) {
  const refresh = useRefreshSupport()
  return useMutation({ mutationFn: () => supportApi.close(id), onSuccess: refresh })
}

// ── Super Admin hooks ───────────────────────────────────────────────────────

export function useDeskTickets(filters: DeskFilters) {
  return useQuery({
    queryKey: keys.admin.supportList(filters as Record<string, unknown>),
    queryFn: () => deskApi.list(filters),
    refetchInterval: 20_000,
  })
}

export function useDeskCounts() {
  return useQuery({
    queryKey: keys.admin.supportCounts(),
    queryFn: deskApi.counts,
    refetchInterval: 20_000,
    retry: false,
  })
}

export function useDeskTicket(id: string | undefined) {
  return useQuery({
    queryKey: keys.admin.supportTicket(id ?? ''),
    queryFn: () => deskApi.get(id!),
    enabled: Boolean(id),
    refetchInterval: 8_000,
  })
}

export function useDeskActions(id: string) {
  const refresh = useRefreshSupport()
  const done = { onSuccess: refresh }
  return {
    verify: useMutation({ mutationFn: (note?: string) => deskApi.verify(id, note), ...done }),
    reply: useMutation({
      mutationFn: ({
        text,
        internal,
        photoIds,
      }: {
        text: string
        internal: boolean
        photoIds: string[]
      }) => deskApi.reply(id, text, internal, photoIds),
      ...done,
    }),
    setStatus: useMutation({
      mutationFn: ({ status, reason }: { status: TicketStatus; reason?: string }) =>
        deskApi.setStatus(id, status, reason),
      ...done,
    }),
    setPriority: useMutation({
      mutationFn: (priority: TicketPriority) => deskApi.setPriority(id, priority),
      ...done,
    }),
    assign: useMutation({
      mutationFn: (assigneeMe: boolean) => deskApi.assign(id, assigneeMe),
      ...done,
    }),
  }
}
